import type { Condition } from '@treetrack/protocol';
import { CONDITION_COLORS, UNMEASURED_COLOR } from '@treetrack/protocol';
import { h, type Child } from '../dom';
import { t } from '../i18n';

export function card(title: string | null, ...children: Child[]): HTMLElement {
  return h('section', { class: 'card' }, title ? h('h2', null, title) : null, ...children);
}

export function empty(text: string = t('dashboard.no_data')): HTMLElement {
  return h('p', { class: 'empty' }, text);
}

export function errorBox(err: unknown): HTMLElement {
  const msg = err instanceof Error ? err.message : String(err);
  return h('div', { class: 'error' }, `${t('common.error')}: ${msg}`);
}

export function loading(): HTMLElement {
  return h('p', { class: 'loading' }, t('common.loading'));
}

export function table(headers: string[], rows: Child[][], opts: { class?: string } = {}): HTMLElement {
  return h(
    'div',
    { class: 'table-wrap' },
    h(
      'table',
      { class: opts.class ?? '' },
      h('thead', null, h('tr', null, ...headers.map((x) => h('th', null, x)))),
      h('tbody', null, ...rows.map((r) => h('tr', null, ...r.map((c) => h('td', null, c))))),
    ),
  );
}

export function conditionBadge(c: Condition | null | undefined): HTMLElement {
  const col = c ? CONDITION_COLORS[c] : UNMEASURED_COLOR;
  return h(
    'span',
    { class: 'badge', style: { background: col.fill, color: col.hollow ? col.stroke : '#fff', border: `2px solid ${col.stroke}` } },
    c ? t(`condition.${c}`) : t('map.unmeasured'),
  );
}

export function labelled(label: string, input: HTMLElement, hint?: string): HTMLElement {
  return h('label', { class: 'field' }, h('span', { class: 'field-label' }, label), input, hint ? h('span', { class: 'hint' }, hint) : null);
}

export function input(attrs: Record<string, unknown>): HTMLInputElement {
  return h('input', { class: 'input', ...attrs });
}

export function select(options: Array<{ value: string; label: string }>, attrs: Record<string, unknown> = {}): HTMLSelectElement {
  const sel = h('select', { class: 'input', ...attrs });
  for (const o of options) sel.appendChild(h('option', { value: o.value }, o.label));
  if (attrs.value != null) sel.value = String(attrs.value);
  return sel;
}

export function button(label: string, onClick: (e: Event) => void, cls = 'btn btn-primary', attrs: Record<string, unknown> = {}): HTMLButtonElement {
  return h('button', { type: 'button', class: cls, onclick: onClick, ...attrs }, label);
}

/** Toggleable chip buttons bound to a Set. */
export function chips(options: Array<{ value: string; label: string }>, selected: Set<string>): HTMLElement {
  const wrap = h('div', { class: 'chips' });
  for (const o of options) {
    const b = h('button', { type: 'button', class: `chip${selected.has(o.value) ? ' on' : ''}` }, o.label);
    b.addEventListener('click', () => {
      if (selected.has(o.value)) selected.delete(o.value);
      else selected.add(o.value);
      b.classList.toggle('on', selected.has(o.value));
    });
    wrap.appendChild(b);
  }
  return wrap;
}

export function pct(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  return `${Number(v).toFixed(1).replace(/\.0$/, '')}%`;
}
